'use client';

import { useMemo } from 'react';
import type { CartItem } from '@/features/totems/domain/model/cart-item';
import { calculateCartTotals, formatTotemPrice } from '@/features/totems/domain/service/totem-price';
import { cn } from '@/shared/utils/utils';

type Props = {
  items: CartItem[];
  /** Cupón de carrito aplicado (solo se usan code/amount/type). */
  discount?: { code: string; amount: number; type: 'percentage' | 'fixed' } | null;
  /** Tema oscuro (drawer mobile / barra de la carta). */
  dark?: boolean;
  className?: string;
};

/**
 * Desglose canónico del carrito (misma fuente que `useTotemCart`):
 * `Subtotal` (bruto) / `Promo` (ahorro por línea) / `Cupón (código)` / `Total`.
 * Se oculta cuando no hay promos ni cupón.
 */
export function CartTotalsBreakdown({ items, discount, dark, className }: Props) {
  const totals = useMemo(() => calculateCartTotals(items, discount ?? null), [items, discount]);

  if (totals.promoDiscountAmount <= 0 && !discount) return null;

  return (
    <div className={cn('flex flex-col gap-1 text-sm', className)}>
      <div className={cn('flex items-center justify-between', dark ? 'text-zinc-400' : 'text-muted-foreground')}>
        <span>Subtotal</span>
        <span>{formatTotemPrice(totals.subtotalBruto)}</span>
      </div>
      {totals.promoDiscountAmount > 0 && (
        <div
          className={cn(
            'flex items-center justify-between font-medium',
            dark ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400'
          )}
        >
          <span>Promo</span>
          <span>−{formatTotemPrice(totals.promoDiscountAmount)}</span>
        </div>
      )}
      {discount && (
        <div
          className={cn(
            'flex items-center justify-between font-medium',
            dark ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400'
          )}
        >
          <span>Cupón ({discount.code})</span>
          <span>−{formatTotemPrice(totals.couponDiscountAmount)}</span>
        </div>
      )}
    </div>
  );
}
